import { createFocusTrap } from 'focus-trap';
import React, { useRef, useEffect, useReducer, useCallback, createElement, useLayoutEffect, useState, Fragment } from 'react';
import { createPortal } from 'react-dom';

function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  return Constructor;
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _unsupportedIterableToArray(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  if (typeof Symbol === "undefined" || !(Symbol.iterator in Object(arr))) return;
  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}

function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;

  for (var i = 0, arr2 = new Array(len); i < len; i++) arr2[i] = arr[i];

  return arr2;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}

var IS_SERVER = typeof window === "undefined" || !!(typeof Deno !== "undefined" && Deno && Deno.version && Deno.version.deno);
var usePrevious = function usePrevious(value) {
  var prev = useRef({
    previous: value
  });
  useEffect(function () {
    prev.current = {
      previous: value
    };
  }, [value]);
  return prev.current;
};
var createKey = function createKey() {
  return Math.random().toString(36).substr(2, 8);
};

var MordredEntry = /*#__PURE__*/function () {
  function MordredEntry(key, options, updated, destroyed) {
    _classCallCheck(this, MordredEntry);

    this.key = key;
    this.options = options;
    this.updated = updated;
    this.destroyed = destroyed;
  }

  _createClass(MordredEntry, [{
    key: "update",
    value: function update(options) {
      Object.assign(this.options, options);
      this.updated();
    }
  }, {
    key: "close",
    value: function close() {
      this.destroyed(this);
    }
  }, {
    key: "element",
    get: function get() {
      return this.options.element;
    }
  }, {
    key: "clickBackdropToClose",
    get: function get() {
      return !!this.options.clickBackdropToClose;
    }
  }]);

  return MordredEntry;
}();
var Mordred = /*#__PURE__*/function () {
  function Mordred() {
    var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};

    _classCallCheck(this, Mordred);

    var _a;

    this.rootElement = null;
    this.focusTrap = null;
    this.modals = new Map();
    this.activeEntriesCache = [];
    this.observer = new Set();
    this.options = options;
    if (IS_SERVER) return;

    if (options.rootElement) {
      this.rootElement = options.rootElement;
    } else {
      var div = this.rootElement = document.createElement("div");
      div.className = "mordred-context";
      Object.assign(div.style, {
        position: "fixed",
        top: 0,
        left: 0,
        zIndex: (_a = options.zIndex) !== null && _a !== void 0 ? _a : Number.MAX_SAFE_INTEGER,
        display: "block",
        height: "0",
        width: "0",
        overflow: "visible"
      });
      document.body.appendChild(div);
    }

    if (!options.disableFocusTrap) {
      this.initFocusTrap();
    }
  }

  _createClass(Mordred, [{
    key: "openModal",
    value: function openModal(option) {
      var _this = this;

      if (IS_SERVER) {
        throw new Error("Mordred: Can't open modal in Server side, please move openModal inside useEffect or componentDidMount");
      }

      var key = createKey();
      var entry = new MordredEntry(key, Object.assign({
        key: key
      }, option), function () {
        return _this.dispatchUpdate();
      }, function (entry) {
        return _this.destroyModal(entry.key);
      });
      this.modals.set(key, entry);
      this.activeEntriesCache = Array.from(this.modals.values());
      this.dispatchUpdate();
      return entry;
    }
  }, {
    key: "changeSetting",
    value: function changeSetting(option) {
      var _a;

      this.options.allowMultipleModals = !!option.allowMultipleModals;
      this.options.zIndex = option.zIndex;

      if (this.options.disableFocusTrap && !option.disableFocusTrap) {
        this.initFocusTrap();
      }

      if (!this.options.disableFocusTrap && option.disableFocusTrap) {
        (_a = this.focusTrap) === null || _a === void 0 ? void 0 : _a.deactivate();
        this.focusTrap = null;
      }

      this.dispatchUpdate();
    }
  }, {
    key: "destroyModal",
    value: function destroyModal(key) {
      this.modals["delete"](key);
      this.activeEntriesCache = Array.from(this.modals.values());
      this.dispatchUpdate();
    }
  }, {
    key: "recache",
    value: function recache() {
      var entries = Array.from(this.modals.values());
      this.activeEntriesCache = this.options.allowMultipleModals ? entries : entries.slice(-1);
    }
  }, {
    key: "observe",
    value: function observe(listener) {
      this.observer.add(listener);
    }
  }, {
    key: "unobserve",
    value: function unobserve(listener) {
      this.observer["delete"](listener);
    }
  }, {
    key: "initFocusTrap",
    value: function initFocusTrap() {
      this.focusTrap = createFocusTrap(this.rootElement, {
        fallbackFocus: this.rootElement
      });
    }
  }, {
    key: "dispatchUpdate",
    value: function dispatchUpdate() {
      if (IS_SERVER) return;
      this.recache();
      this.observer.forEach(function (observer) {
        return observer();
      });
    }
  }, {
    key: "activeEntries",
    get: function get() {
      return this.activeEntriesCache;
    }
  }], [{
    key: "init",
    value: function init() {
      var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
      if (Mordred._instance) throw new Error("Mordred is already initialized");
      Mordred._instance = new Mordred(options);
    }
  }, {
    key: "instance",
    get: function get() {
      if (!Mordred._instance) {
        throw new Error("Mordred: Mordred is not initialized, Please call `Mordred.init` first");
      }

      return Mordred._instance;
    }
  }]);

  return Mordred;
}();

function __rest(s, e) {
  var t = {};

  for (var p in s) {
    if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  }

  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}
function __awaiter(thisArg, _arguments, P, generator) {
  function adopt(value) {
    return value instanceof P ? value : new P(function (resolve) {
      resolve(value);
    });
  }

  return new (P || (P = Promise))(function (resolve, reject) {
    function fulfilled(value) {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    }

    function rejected(value) {
      try {
        step(generator["throw"](value));
      } catch (e) {
        reject(e);
      }
    }

    function step(result) {
      result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected);
    }

    step((generator = generator.apply(thisArg, _arguments || [])).next());
  });
}

/**
 * Stable API.
 *
 * But use it "anyplace" in your application makes very complex Modal state.
 * (Maybe, can't keep "Single modal same time". It's not so good UX.)
 * Recommended, `useModalOpener` and pass it returned function to ActionCreator instead.
 */

var unrecommended_openModal = function unrecommended_openModal(Component, props) {
  var _ref = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {},
      signal = _ref.signal;

  return __awaiter(void 0, void 0, void 0, function* () {
    return new Promise(function (resolve) {
      var handleAbort = function handleAbort() {
        entry.close();
        resolve(void 0);
      };

      var entry = Mordred.instance.openModal({
        clickBackdropToClose: !!props.clickBackdropToClose,
        element: createElement(Component, Object.assign(Object.assign({}, props), {
          onClose: function onClose(result) {
            resolve(result);
            signal === null || signal === void 0 ? void 0 : signal.removeEventListener("abort", handleAbort);
            entry.close();
          }
        }))
      });
      signal === null || signal === void 0 ? void 0 : signal.addEventListener("abort", handleAbort);
    });
  });
};
var useModalsQueue = function useModalsQueue() {
  var _useReducer = useReducer(function (s) {
    return s + 1;
  }, 0),
      _useReducer2 = _slicedToArray(_useReducer, 2),
      rerender = _useReducer2[1];

  var elements = useRef(null);
  var update = useCallback(function () {
    elements.current = React.createElement(React.Fragment, null, Mordred.instance.activeEntries.map(function (entry) {
      return entry.element;
    }));
    rerender();
  }, []);
  useEffect(function () {
    Mordred.instance.observe(update);
    return function () {
      return Mordred.instance.unobserve(update);
    };
  }, [update]);
  return {
    hasModal: Mordred.instance.activeEntries.length != 0,
    modalElements: elements.current,
    modalEntries: Mordred.instance.activeEntries
  };
};
/**
 * Use `openModal` function in React.

 * Modals mounted by these hooks will be closed when the component is unmounted.
 */

var useModalOpener = function useModalOpener() {
  var aborts = useRef([]);
  var modalOpener = useCallback(function (Component, props) {
    var _ref2 = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {},
        signal = _ref2.signal;

    // Will use AbortController, however AbortController is not available in server side. Let guard.
    if (IS_SERVER) {
      throw new Error("Mordred: Can't open modal in Server side, please move openModal inside useEffect or componentDidMount");
    }

    var controller = new AbortController();
    signal === null || signal === void 0 ? void 0 : signal.addEventListener("abort", controller.abort);
    var entry = unrecommended_openModal(Component, props, {
      signal: controller.signal
    });
    aborts.current.push(controller);
    return entry;
  }, []);
  useEffect(function () {
    return function () {
      return aborts.current.forEach(function (controller) {
        return controller.abort();
      });
    };
  }, []);
  return {
    openModal: modalOpener
  };
};

var Modal = function Modal(_a) {
  var component = _a.component,
      props = _a.props,
      _a$clickBackdropToClo = _a.clickBackdropToClose,
      clickBackdropToClose = _a$clickBackdropToClo === void 0 ? true : _a$clickBackdropToClo,
      rest = __rest(_a, ["component", "props", "clickBackdropToClose"]);

  var prevIsOpen = usePrevious(rest.isOpen);
  var entry = useRef(null);
  var handleClose = useCallback(function (result) {
    var _a, _b;

    (_a = entry.current) === null || _a === void 0 ? void 0 : _a.close();
    (_b = rest.onClose) === null || _b === void 0 ? void 0 : _b.call(rest, result);
  }, [rest.onClose]);
  useLayoutEffect(function () {
    var _a;

    if (prevIsOpen.previous !== rest.isOpen) {
      // Change to close
      if (rest.isOpen == false) {
        (_a = entry.current) === null || _a === void 0 ? void 0 : _a.close();
        entry.current = null;
      }
    }

    if (!rest.isOpen) return;

    if (entry.current) {
      entry.current.update({
        element: rest.children,
        onAfterOpen: rest.onAfterOpen,
        clickBackdropToClose: clickBackdropToClose
      });
    } else {
      var Comp = component;
      entry.current = Mordred.instance.openModal({
        element: React.createElement(Comp, Object.assign({}, props, {
          isOpen: rest.isOpen,
          children: rest.children,
          onAfterOpen: rest.onAfterOpen,
          clickBackdropToClose: clickBackdropToClose,
          onClose: handleClose
        }))
      });
    }
  }, [component, rest.isOpen, clickBackdropToClose]);
  return null;
};

var MordredRenderer = function MordredRenderer(_ref) {
  var _ref$children = _ref.children,
      children = _ref$children === void 0 ? function (_ref2) {
    var children = _ref2.children;
    return children;
  } : _ref$children;

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      mounted = _useState2[0],
      setMounted = _useState2[1];

  var _useReducer = useReducer(function (s) {
    return s + 1;
  }, 0),
      _useReducer2 = _slicedToArray(_useReducer, 2),
      rerender = _useReducer2[1];

  var styleRef = useRef(null);
  var instance = Mordred.instance;
  var entries = instance.activeEntries;
  var onUpdate = useCallback(function () {
    var _a, _b, _c;

    if (instance.activeEntries.length > 0 && styleRef.current == null) {
      var style = styleRef.current = document.createElement("style");
      style.textContent = "body { overflow: hidden /* Lock by Mordred */; }";
      document.head.appendChild(style);
      (_a = document.activeElement) === null || _a === void 0 ? void 0 : _a.blur();
      (_b = instance.focusTrap) === null || _b === void 0 ? void 0 : _b.activate();
    }

    if (instance.activeEntries.length === 0 && styleRef.current != null) {
      styleRef.current.remove();
      styleRef.current = null;
      (_c = instance.focusTrap) === null || _c === void 0 ? void 0 : _c.deactivate();
    }

    rerender();
  }, []);
  useEffect(function () {
    setMounted(true);
    instance.observe(onUpdate);
    return function () {
      return instance.unobserve(onUpdate);
    };
  }, []);

  if (IS_SERVER || !mounted || !instance.rootElement) {
    return React.createElement(React.Fragment, null);
  }

  if (typeof children === "function") {
    return React.createElement(React.Fragment, null, createPortal(entries.map(function (entry) {
      return React.createElement(Fragment, {
        key: entry.key
      }, children({
        children: entry.element,
        entry: entry,
        closeCurrent: function closeCurrent() {
          return entry.close();
        }
      }));
    }), Mordred.instance.rootElement));
  }

  if (children !== null) {
    return React.createElement(React.Fragment, null, createPortal(children, instance.rootElement));
  }

  return React.createElement(React.Fragment, null, createPortal(React.createElement(React.Fragment, null, entries.map(function (entry) {
    return React.createElement(Fragment, {
      key: entry.key
    }, entry.element);
  })), instance.rootElement));
};

var whenExactlyClickThen = function whenExactlyClickThen(handler) {
  return function (e) {
    if (e.currentTarget === e.target) handler(e);
  };
};

export { Modal, Mordred, MordredEntry, MordredRenderer, unrecommended_openModal, useModalOpener, useModalsQueue, whenExactlyClickThen };
